/**
 * Push Rule State Utility Module
 *
 * Provides utilities for parsing and managing Matrix push rule states
 * with three notification levels: notify (default), mentionsOnly, and dontNotify.
 *
 * Matrix push rules have a priority order:
 * 1. Override rules (highest priority) - used for full mute (dontNotify)
 * 2. Room rules - used for mentions-only
 * 3. Content rules
 * 4. Underride rules (lowest priority)
 */
import logger from './logger';

/**
 * Push rule states for room notifications
 * @readonly
 * @enum {string}
 */
export const PushRuleState = Object.freeze({
  /** All notifications enabled (default behavior) */
  NOTIFY: 'notify',
  /** Only @mentions trigger notifications */
  MENTIONS_ONLY: 'mentionsOnly',
  /** Fully muted - no notifications */
  DONT_NOTIFY: 'dontNotify',
});

/**
 * Validates if a state is a valid PushRuleState value
 * @param {string} state - The state to validate
 * @returns {boolean} True if valid PushRuleState value
 */
export function isValidPushRuleState(state) {
  return Object.values(PushRuleState).includes(state);
}

/**
 * Checks if a rule's actions indicate "don't notify"
 * Actions can be an empty array [] or contain 'dont_notify' string
 * @param {Array} actions - The rule's actions array
 * @returns {boolean} True if actions indicate don't notify
 */
function isDontNotifyAction(actions) {
  if (!Array.isArray(actions)) return false;
  // Empty actions array means don't notify
  if (actions.length === 0) return true;
  // Explicit 'dont_notify' action
  return actions.includes('dont_notify');
}

/**
 * Gets the current push rule state for a room
 *
 * Checks Matrix push rules in priority order:
 * 1. Override rules (for full mute - DONT_NOTIFY)
 * 2. Room rules (for mentions-only - MENTIONS_ONLY)
 *
 * @param {Object} client - Matrix client instance
 * @param {string} roomId - The room ID to check
 * @returns {string} The current PushRuleState value
 */
export function getRoomPushRuleState(client, roomId) {
  // Null/undefined safety - return default
  if (!client || !roomId) {
    logger.debug('[pushRuleState] getRoomPushRuleState: Invalid client or roomId, returning NOTIFY');
    return PushRuleState.NOTIFY;
  }

  try {
    const globalRules = client.pushRules?.global;
    if (!globalRules) {
      logger.debug('[pushRuleState] No global push rules found, returning NOTIFY');
      return PushRuleState.NOTIFY;
    }

    // Check override rules first (highest priority) for full mute
    const overrideRules = globalRules.override;
    if (Array.isArray(overrideRules)) {
      const overrideRule = overrideRules.find(rule => rule.rule_id === roomId);
      if (overrideRule && overrideRule.enabled !== false) {
        if (isDontNotifyAction(overrideRule.actions)) {
          logger.debug(`[pushRuleState] Found enabled override rule for ${roomId}, returning DONT_NOTIFY`);
          return PushRuleState.DONT_NOTIFY;
        }
      }
    }

    // Check room rules second for mentions-only
    const roomRules = globalRules.room;
    if (Array.isArray(roomRules)) {
      const roomRule = roomRules.find(rule => rule.rule_id === roomId);
      if (roomRule && roomRule.enabled !== false) {
        if (isDontNotifyAction(roomRule.actions)) {
          logger.debug(`[pushRuleState] Found enabled room rule for ${roomId}, returning MENTIONS_ONLY`);
          return PushRuleState.MENTIONS_ONLY;
        }
      }
    }

    // Default: all notifications enabled
    logger.debug(`[pushRuleState] No matching rules for ${roomId}, returning NOTIFY`);
    return PushRuleState.NOTIFY;
  } catch (error) {
    logger.error('[pushRuleState] Error getting room push rule state:', error);
    return PushRuleState.NOTIFY;
  }
}

/**
 * Sets the push rule state for a room
 *
 * Modifies Matrix push rules to achieve the desired notification state:
 * - DONT_NOTIFY: Adds an override rule that suppresses all notifications
 * - MENTIONS_ONLY: Adds a room rule that only allows @mentions
 * - NOTIFY: Removes any custom rules to restore default behavior
 *
 * @param {Object} client - Matrix client instance
 * @param {string} roomId - The room ID to modify
 * @param {string} newState - The desired PushRuleState value
 * @param {string} [currentState] - Optional current state (will be detected if not provided)
 * @returns {Promise<string>} The new state after modification
 * @throws {Error} If client or roomId is invalid, or if state change fails
 */
export async function setRoomPushRuleState(client, roomId, newState, currentState) {
  // Validate inputs
  if (!client) {
    throw new Error('setRoomPushRuleState: client is required');
  }
  if (!roomId || typeof roomId !== 'string') {
    throw new Error('setRoomPushRuleState: valid roomId is required');
  }
  if (!isValidPushRuleState(newState)) {
    throw new Error(`setRoomPushRuleState: invalid newState "${newState}"`);
  }

  // If currentState not provided, detect it
  const effectiveCurrentState = currentState ?? getRoomPushRuleState(client, roomId);

  // Validate currentState if provided
  if (currentState && !isValidPushRuleState(currentState)) {
    logger.warn(`[pushRuleState] Invalid currentState "${currentState}", detecting automatically`);
  }

  // If already in desired state, no action needed
  if (effectiveCurrentState === newState) {
    logger.debug(`[pushRuleState] Room ${roomId} already in state ${newState}, no change needed`);
    return newState;
  }

  logger.info(`[pushRuleState] Changing room ${roomId} from ${effectiveCurrentState} to ${newState}`);

  try {
    // Step 1: Delete old rules based on current state
    await deleteExistingRules(client, roomId, effectiveCurrentState);

    // Step 2: Add new rules based on new state
    await addNewRules(client, roomId, newState);

    logger.info(`[pushRuleState] Successfully changed room ${roomId} to ${newState}`);
    return newState;
  } catch (error) {
    logger.error(`[pushRuleState] Failed to set room ${roomId} to ${newState}:`, error);
    throw error;
  }
}

/**
 * Deletes existing push rules for a room based on current state
 * @param {Object} client - Matrix client instance
 * @param {string} roomId - The room ID
 * @param {string} currentState - The current PushRuleState
 */
async function deleteExistingRules(client, roomId, currentState) {
  try {
    if (currentState === PushRuleState.DONT_NOTIFY) {
      // Delete override rule
      logger.debug(`[pushRuleState] Deleting override rule for ${roomId}`);
      await client.deletePushRule('global', 'override', roomId);
    } else if (currentState === PushRuleState.MENTIONS_ONLY) {
      // Delete room rule
      logger.debug(`[pushRuleState] Deleting room rule for ${roomId}`);
      await client.deletePushRule('global', 'room', roomId);
    }
    // NOTIFY state has no custom rules to delete
  } catch (error) {
    // 404 errors are expected when rule doesn't exist - ignore them
    if (error.httpStatus === 404 || error.errcode === 'M_NOT_FOUND') {
      logger.debug(`[pushRuleState] Rule not found for ${roomId}, continuing`);
    } else {
      // Re-throw unexpected errors
      throw error;
    }
  }
}

/**
 * Adds new push rules for a room based on desired state
 * @param {Object} client - Matrix client instance
 * @param {string} roomId - The room ID
 * @param {string} newState - The desired PushRuleState
 */
async function addNewRules(client, roomId, newState) {
  if (newState === PushRuleState.DONT_NOTIFY) {
    // Add override rule for full mute
    logger.debug(`[pushRuleState] Adding override rule for ${roomId}`);
    await client.addPushRule('global', 'override', roomId, {
      actions: [],
      conditions: [
        {
          kind: 'event_match',
          key: 'room_id',
          pattern: roomId,
        },
      ],
    });
  } else if (newState === PushRuleState.MENTIONS_ONLY) {
    // Add room rule for mentions-only
    logger.debug(`[pushRuleState] Adding room rule for ${roomId}`);
    await client.addPushRule('global', 'room', roomId, {
      actions: [],
    });
  }
  // NOTIFY state requires no new rules - just delete existing ones
}
